import { isLiveRegisterRow } from './register-status.js';

// Deterministic document-control checks. These deliberately inspect register
// data only: policy interpretation belongs to the source-grounded Ask Track
// service, not to browser heuristics or model memory.
const clean = (v) => String(v || '').trim();
const lower = (v) => clean(v).toLowerCase();

function finding(level, code, title, detail, documentId = '') {
  return { level, code, title, detail, documentId };
}

export function auditDocumentControl(project, documents = []) {
  const projectNo = clean(project?.number);
  const active = documents.filter((d) => d.direction !== 'received' && isLiveRegisterRow(d));   // superseded and binned rows are off the register
  const findings = [];
  const byNumber = new Map();

  for (const d of active) {
    const label = clean(d.filename) || 'Unnamed document';
    if (!clean(d.filename)) findings.push(finding('blocked', 'missing-filename', 'Filename is missing', 'The register entry cannot be traced to a named file.', d.id));
    if (!clean(d.docNumber) && d.type !== 'site-media') findings.push(finding('blocked', 'missing-number', 'Document number is missing', label, d.id));
    // Revision policy (Karan 08/08): a revision is only REQUIRED once a document
    // has been revised — a first issue with no revision letter is normal, not a
    // block. The inconsistency case (same number, some entries revised, this one
    // not) is caught in the byNumber pass below.
    if (projectNo && clean(d.projectNo) && clean(d.projectNo) !== projectNo) findings.push(finding('blocked', 'project-mismatch', 'Project number does not match', `${label} is registered to ${clean(d.projectNo)}, not ${projectNo}.`, d.id));
    if (d.needsReview) findings.push(finding('warning', 'register-review', 'Register entry needs review', label, d.id));
    if (!clean(d.category) && d.type !== 'site-media') findings.push(finding('warning', 'missing-category', 'Document category is missing', label, d.id));

    const no = lower(d.docNumber);
    if (no) {
      if (!byNumber.has(no)) byNumber.set(no, []);
      byNumber.get(no).push(d);
    }
  }

  for (const docs of byNumber.values()) {
    if (docs.length < 2) continue;
    const revisions = new Set(docs.map((d) => lower(d.revision)).filter(Boolean));
    if (revisions.size > 1) findings.push(finding('blocked', 'multiple-current-revisions', 'Multiple revisions appear current', `${clean(docs[0].docNumber)} has current revisions ${[...revisions].join(', ')}. Confirm supersession.`));
    else findings.push(finding('warning', 'duplicate-current-entry', 'Duplicate current register entries', `${clean(docs[0].docNumber)} appears ${docs.length} times.`));
    // Same number carries revisions elsewhere but not here → THAT is the
    // missing-revision case worth flagging (see policy note above).
    if (revisions.size) {
      docs.filter((d) => !clean(d.revision) && d.type !== 'site-media')
        .forEach((d) => findings.push(finding('warning', 'missing-revision', 'Revision missing on a revised document', `${clean(d.filename) || clean(docs[0].docNumber)} — other entries of ${clean(docs[0].docNumber)} carry a revision.`, d.id)));
    }
  }

  const blocked = findings.filter((x) => x.level === 'blocked').length;
  const warnings = findings.filter((x) => x.level === 'warning').length;
  return {
    status: blocked ? 'blocked' : warnings ? 'warning' : 'pass',
    blocked, warnings, checked: active.length, findings,
  };
}

export function policyQuestion(project, audit) {
  const lines = audit.findings.slice(0, 12).map((f) => `- ${f.title}: ${f.detail}`);
  return `Check these document-control findings for ASP project ${clean(project?.number)} against the current approved ASP Quality Management and inspection procedures. Cite the exact controlled document, revision and page for every requirement. Distinguish mandatory procedure from good practice, and do not infer a requirement that is not in the sources.\n\n${lines.join('\n')}`;
}
